import getSuggestions from '../actions/getSuggestions';
import initializeLazyScenarioStore from '../actions/initializeLazyScenarioStore';
import onSearchInputClicked from '../actions/onSearchInputClicked';
import onSearchInputFocused from '../actions/onSearchInputFocused';
import onSearchTextChanged from '../actions/onSearchTextChanged';
import { SUGGESTION_ID_PREFIX, SUGGESTIONS_CALLOUT_ID } from '../constants';
import isSearchSessionActive from '../selectors/isSearchSessionActive';
import getScenarioStore from '../store/getScenarioStore';
import getPlaceholderAndAriaLabel from '../utils/getPlaceholderAndAriaLabel';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import KeyboardCharCodes from 'owa-hotkeys/lib/utils/keyboardCharCodes';
import { SearchScenarioId } from 'owa-search-next-common';
import * as React from 'react';
import onKeyDownSearchInput, {
    onBackspacePressedSearchInput,
    onDownArrowPressedSearchInput,
    onEnterPressedSearchInput,
    onEscapePressedSearchInput,
    onLeftArrowPressedSearchInput,
    onUpArrowPressedSearchInput,
} from '../actions/onKeyDownSearchInput';

const classNames = require('classnames');
const styles = require('./SearchInput.scss');

export interface SearchInputProps {
    searchPlaceHolderText?: string;
    isDisabled?: boolean; // Boolean indicating if search box is disabled (default is false)
    scenarioId: SearchScenarioId;
    isSuggestionsCalloutVisible: boolean;
    isUsing3S: boolean;
}

export interface ISearchInput {
    setFocus(): void;
    searchInputText: string;
    activeDescendant: string;
}

@observer
export default class SearchInput extends React.Component<SearchInputProps, {}>
    implements ISearchInput {
    private searchInput: HTMLInputElement;

    /**
     * Sets focus on the search input. This is exposed publicly so parent component
     * (SearchBox) can set focus on the input element as a result of clicks on other
     * parts of the search box.
     */
    public setFocus() {
        if (this.searchInput) {
            this.searchInput.focus();
        }
    }

    /**
     * This computed returns the text to be displayed in the actual input
     * element.
     */
    @computed
    get searchInputText(): string {
        return getScenarioStore(this.props.scenarioId).searchText;
    }

    @computed
    get activeDescendant(): string {
        const { isSuggestionsCalloutVisible, scenarioId } = this.props;
        const { selectedSuggestionIndex } = getScenarioStore(scenarioId);

        /**
         * If search suggestions are visible and the user has a suggestion
         * selected, then return a string to be used for the component's
         * aria-activedescendant attribute.
         */
        if (isSuggestionsCalloutVisible && selectedSuggestionIndex >= 0) {
            return `${SUGGESTION_ID_PREFIX}${selectedSuggestionIndex}`;
        }

        return '';
    }

    componentDidMount() {
        initializeLazyScenarioStore(this.props.scenarioId, this.props.isUsing3S);
    }

    render() {
        const {
            scenarioId,
            searchPlaceHolderText,
            isDisabled,
            isSuggestionsCalloutVisible,
        } = this.props;

        const searchInputClasses = classNames(
            styles.searchBoxInput,
            isDisabled && styles.isDisabled,
            isSearchSessionActive(scenarioId)
                ? styles.inSearchinputPlaceholder
                : styles.inputPlaceholder
        );

        const { ariaLabel, placeholder } = getPlaceholderAndAriaLabel(
            scenarioId,
            searchPlaceHolderText
        );

        return (
            <div className={styles.searchBoxInputContainer}>
                <input
                    aria-activedescendant={this.activeDescendant}
                    aria-autocomplete="list"
                    aria-label={ariaLabel}
                    aria-owns={isSuggestionsCalloutVisible ? SUGGESTIONS_CALLOUT_ID : ''}
                    autoComplete="off"
                    className={searchInputClasses}
                    disabled={isDisabled}
                    onChange={this.onSearchTextChange}
                    onClick={this.onClick}
                    onFocus={this.onFocus}
                    onKeyDown={this.onKeyDown}
                    placeholder={placeholder}
                    ref={ref => (this.searchInput = ref)}
                    value={this.searchInputText}
                    spellCheck={false}
                    autoCorrect="off"
                />
            </div>
        );
    }

    /**
     * Key handler for special key input into the input element.
     */
    private onKeyDown = async (evt: React.KeyboardEvent<unknown>) => {
        evt.stopPropagation();
        const evtKeyCode = evt.keyCode;

        const scenarioId = this.props.scenarioId;
        const { selectedSuggestionIndex, isSuggestionsDropdownVisible } = getScenarioStore(
            scenarioId
        );

        onKeyDownSearchInput(evtKeyCode, scenarioId);

        switch (evtKeyCode) {
            case KeyboardCharCodes.Enter: {
                // Clear selection (non-default behavior of IE/Edge).
                this.clearSelection();

                onEnterPressedSearchInput(selectedSuggestionIndex, scenarioId);
                break;
            }
            case KeyboardCharCodes.Backspace: {
                onBackspacePressedSearchInput(scenarioId, this.getCursorPosition());
                break;
            }
            case KeyboardCharCodes.Up_arrow:
                // Prevents cursor from going to beginning of input.
                evt.preventDefault();

                onUpArrowPressedSearchInput(scenarioId);
                break;
            case KeyboardCharCodes.Down_arrow:
                onDownArrowPressedSearchInput(scenarioId);
                break;
            case KeyboardCharCodes.Left_arrow: {
                onLeftArrowPressedSearchInput(this.getCursorPosition(), scenarioId);
                break;
            }
            case KeyboardCharCodes.Esc: {
                // Prevents IE/Edge behavior of putting last text back into the input.
                evt.preventDefault();

                onEscapePressedSearchInput(scenarioId, isSuggestionsDropdownVisible);
                break;
            }
            default:
                break;
        }
    };

    /**
     * Click handler when user clicks in input.
     */
    private onClick = (evt: React.MouseEvent<unknown>) => {
        evt.stopPropagation();
        onSearchInputClicked(this.props.scenarioId);
    };

    /**
     * Focus handler for when the SearchInput component receives focus.
     */
    private onFocus = () => {
        onSearchInputFocused(this.props.scenarioId, this.props.isUsing3S);
    };

    /**
     * Need to do this synchronously or there'll be rendering issues and the value
     * of the input element will be empty.
     */
    private onSearchTextChange = () => {
        const scenarioId = this.props.scenarioId;
        const searchText = this.searchInput.value;

        onSearchTextChanged(searchText, scenarioId, true /* showSuggestionsUi */);
        getSuggestions(scenarioId);
    };

    /**
     * Gets cursor position if no text is selected. If text is selected, then
     * -1 is returned.
     */
    private getCursorPosition() {
        /**
         * If reference to input is null (component unmounted), return -1
         * indicating that cursor position shouldn't be used.
         */
        if (!this.searchInput) {
            return -1;
        }

        const isTextSelected = this.searchInput.selectionStart !== this.searchInput.selectionEnd;
        return isTextSelected ? -1 : this.searchInput.selectionStart;
    }

    /**
     * Clears selected text in the input.
     */
    private clearSelection() {
        const searchInputEnd = this.searchInput.value.length;
        this.searchInput.setSelectionRange(searchInputEnd, searchInputEnd);
    }
}
